import React, { useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { PublicBrandFooter } from '@/components/public/public-brand-footer';
import { useCancelGuestAppointment, useGuestAppointment } from '@/hooks/use-public-schedule';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';
import { APPOINTMENT_STATUS } from '@/types/appointment';
import { formatAppointmentWhen } from '@/utils/format-appointment';
import { LOCATION_MODE } from '@/types/location-mode';
import {
  openAddressInMaps,
  openMeetingUrl,
  resolveAppointmentLocation,
} from '@/utils/appointment-location';
import { useIconChip } from '@/hooks/use-theme-colors';

const BRAND = '#FD5001';

const STATUS_LABEL: Record<APPOINTMENT_STATUS, string> = {
  [APPOINTMENT_STATUS.PENDING]: 'Aguardando confirmação',
  [APPOINTMENT_STATUS.CONFIRMED]: 'Confirmado',
  [APPOINTMENT_STATUS.CANCELLED]: 'Cancelado',
  [APPOINTMENT_STATUS.COMPLETED]: 'Concluído',
  [APPOINTMENT_STATUS.NO_SHOW]: 'Não compareceu',
};

const STATUS_COLOR: Record<APPOINTMENT_STATUS, string> = {
  [APPOINTMENT_STATUS.PENDING]: '#D97706',
  [APPOINTMENT_STATUS.CONFIRMED]: '#16A34A',
  [APPOINTMENT_STATUS.CANCELLED]: '#DC2626',
  [APPOINTMENT_STATUS.COMPLETED]: '#525252',
  [APPOINTMENT_STATUS.NO_SHOW]: '#DC2626',
};

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <View className="flex-1 items-center justify-center gap-3 bg-neutral-50 p-8">{children}</View>
  );
}

/**
 * Link mágico do convidado (B3): abre o agendamento pelo token recebido no
 * WhatsApp e permite cancelar dentro da janela — sem conta, sem login.
 */
export default function GuestAppointmentScreen() {
  const chip = useIconChip();
  const { appointmentId, token } = useLocalSearchParams<{
    appointmentId: string;
    token: string;
  }>();
  const { addToast } = useToastStore();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const {
    data: appointment,
    isLoading,
    error,
    refetch,
  } = useGuestAppointment(appointmentId, typeof token === 'string' ? token : undefined);
  const cancelAppointment = useCancelGuestAppointment(appointmentId);

  if (isLoading) {
    return (
      <Centered>
        <ActivityIndicator size="large" color={BRAND} />
      </Centered>
    );
  }

  if (error || !appointment || typeof token !== 'string') {
    return (
      <Centered>
        <Ionicons className="text-neutral-400" name="link-outline" size={48} />
        <ThemedText className="text-center font-bold text-xl">Link inválido ou expirado</ThemedText>
        <ThemedText className="text-center text-neutral-500">
          Não conseguimos abrir este agendamento. Fale com o prestador para confirmar ou alterar o
          horário.
        </ThemedText>
      </Centered>
    );
  }

  const location = resolveAppointmentLocation(appointment);
  const cancellable =
    appointment.status === APPOINTMENT_STATUS.PENDING ||
    appointment.status === APPOINTMENT_STATUS.CONFIRMED;

  const handleCancel = () => {
    cancelAppointment.mutate(
      { token },
      {
        onSuccess: () => {
          addToast('Agendamento cancelado.', 'success');
          setConfirmingCancel(false);
          void refetch();
        },
        onError: (err: any) => {
          addToast(
            getApiErrorMessage(err, 'Não foi possível cancelar. Fale com o prestador.'),
            'error',
          );
          setConfirmingCancel(false);
        },
      },
    );
  };

  return (
    <View className="flex-1 bg-neutral-50">
      <PublicBrandHeader />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        className="flex-1 bg-neutral-50"
        contentContainerStyle={{ flexGrow: 1, padding: 20 }}
      >
        <View className="gap-5">
          <View
            className="items-center gap-3 rounded-3xl bg-card p-6"
            style={{ borderCurve: 'continuous' }}
          >
            <View
              className="h-14 w-14 items-center justify-center rounded-full"
              style={{ backgroundColor: chip(STATUS_COLOR[appointment.status], '1A').bg }}
            >
              <Ionicons
                name="calendar-outline"
                size={26}
                color={chip(STATUS_COLOR[appointment.status], '1A').fg}
              />
            </View>
            <View
              className="rounded-full px-3 py-1"
              style={{ backgroundColor: `${STATUS_COLOR[appointment.status]}1A` }}
            >
              <ThemedText
                className="font-bold text-xs"
                style={{ color: STATUS_COLOR[appointment.status] }}
              >
                {STATUS_LABEL[appointment.status]}
              </ThemedText>
            </View>
            <ThemedText className="text-center font-bold text-2xl">
              {appointment.serviceName ?? 'Agendamento'}
            </ThemedText>
            <ThemedText className="text-center text-base text-neutral-600">
              {formatAppointmentWhen(appointment.startAt)}
            </ThemedText>
            {!!location && (
              <View className="flex-row items-center gap-1.5">
                <Ionicons className="text-neutral-500" name={location.icon} size={15} />
                <ThemedText className="text-sm text-neutral-500">{location.label}</ThemedText>
              </View>
            )}
            {location?.mode === LOCATION_MODE.ONLINE && !location.meetingUrl && (
              <ThemedText className="text-center text-xs text-neutral-500">
                O profissional vai enviar o link quando estiver disponível.
              </ThemedText>
            )}
            {!!location?.meetingUrl && (
              <PressableScale
                onPress={() => openMeetingUrl(location.meetingUrl!)}
                className="mt-1 flex-row items-center gap-1.5 rounded-full bg-primary px-5 py-2.5"
              >
                <Ionicons name="videocam" size={16} color="#FFFFFF" />
                <ThemedText className="font-semibold text-sm text-white">
                  Entrar na chamada
                </ThemedText>
              </PressableScale>
            )}
            {!!location?.address && (
              <PressableScale
                onPress={() => openAddressInMaps(location.address!)}
                className="mt-1 flex-row items-center gap-1.5 rounded-full border border-primary/15 bg-primary/5 px-5 py-2.5"
              >
                <Ionicons name="navigate-outline" size={16} color="#FD5001" />
                <ThemedText className="font-semibold text-sm text-primary">Como chegar</ThemedText>
              </PressableScale>
            )}
            {appointment.status === APPOINTMENT_STATUS.CANCELLED && !!appointment.cancelReason && (
              <ThemedText className="text-center text-sm text-neutral-500">
                Motivo: {appointment.cancelReason}
              </ThemedText>
            )}
          </View>

          {cancellable && !confirmingCancel && (
            <PressableScale
              onPress={() => setConfirmingCancel(true)}
              className="w-full items-center rounded-full border border-red-200 bg-card py-4"
              style={{ borderCurve: 'continuous' }}
            >
              <ThemedText className="font-bold text-red-600">Cancelar agendamento</ThemedText>
            </PressableScale>
          )}

          {cancellable && confirmingCancel && (
            <View className="gap-3 rounded-3xl bg-card p-5" style={{ borderCurve: 'continuous' }}>
              <ThemedText className="text-center font-semibold">
                Cancelar este agendamento?
              </ThemedText>
              <ThemedText className="text-center text-sm text-neutral-500">
                O horário volta a ficar disponível para outras pessoas.
              </ThemedText>
              <View className="flex-row gap-3">
                <Pressable
                  disabled={cancelAppointment.isPending}
                  onPress={() => setConfirmingCancel(false)}
                  className="flex-1 items-center rounded-full bg-neutral-100 py-3.5"
                >
                  <ThemedText className="font-semibold text-neutral-600">Voltar</ThemedText>
                </Pressable>
                <Pressable
                  disabled={cancelAppointment.isPending}
                  onPress={handleCancel}
                  className="flex-1 items-center rounded-full bg-red-600 py-3.5"
                >
                  {cancelAppointment.isPending ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <ThemedText className="font-bold text-white">Cancelar</ThemedText>
                  )}
                </Pressable>
              </View>
            </View>
          )}

          {!isAuthenticated && (
            <View className="gap-3 rounded-3xl bg-card p-5" style={{ borderCurve: 'continuous' }}>
              <View className="flex-row items-center gap-3">
                <View
                  className="h-10 w-10 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: chip(BRAND, '1A').bg, borderCurve: 'continuous' }}
                >
                  <Ionicons name="sparkles-outline" size={20} color={chip(BRAND, '1A').fg} />
                </View>
                <ThemedText className="flex-1 font-bold text-lg">
                  Tenha tudo num lugar só
                </ThemedText>
              </View>
              <ThemedText className="text-sm leading-5 text-neutral-500">
                Crie sua conta para remarcar, ver o histórico e agendar em um toque — seus dados já
                estão salvos.
              </ThemedText>
              <PressableScale
                haptic
                onPress={() =>
                  router.push({
                    pathname: '/agendamento/[appointmentId]/conta',
                    params: { appointmentId, token },
                  } as never)
                }
                className="w-full items-center rounded-full py-4"
                style={{ backgroundColor: BRAND, borderCurve: 'continuous' }}
              >
                <ThemedText className="font-bold text-white">Criar minha conta</ThemedText>
              </PressableScale>
            </View>
          )}

          <Pressable onPress={() => router.replace('/' as any)} className="items-center py-2">
            <ThemedText className="font-semibold text-sm text-neutral-500">
              Conhecer a Controlapag
            </ThemedText>
          </Pressable>
        </View>

        <View className="flex-1" />
        <View style={{ marginHorizontal: -20, marginTop: 24 }}>
          <PublicBrandFooter />
        </View>
      </ScrollView>
    </View>
  );
}
